/**
 * ReportAnalysisWatcher — a global "toast on status transition" watcher.
 *
 * Mounted once near the root of the authenticated app (AppShell). It
 * polls `/api/reports/in-progress` using the same `useSmartPoll` hook
 * that powers the Reports tab list — so when the user starts an
 * analysis on the Reports tab, navigates to /dashboard, and the
 * analysis later completes in the background, the user still sees a
 * success toast wherever they happen to be.
 *
 * Status transitions we surface:
 *   • analysing → complete   → ✅ "Analysis complete" success toast
 *   • analysing → failed     → ❌ "Analysis failed" error toast w/ Retry hint
 *
 * We purposely do NOT raise a toast for the first-seen row (that's the
 * one the user just kicked off — the upload dialog already showed a
 * confirmation). We also suppress toasts for rows that were "complete"
 * or "failed" on the very first observation (i.e. historical rows).
 *
 * Only authenticated users get the watcher mounted; it does nothing
 * for logged-out visitors (landing / login pages).
 */
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useSmartPoll from "@/hooks/useSmartPoll";

const TERMINAL_STATES = new Set(["complete", "analysed", "failed", "error"]);

const ReportAnalysisWatcher = () => {
  const { user, isAuthenticated } = useAuth();
  // Map: report_id -> last observed status
  const lastStatusRef = useRef(new Map());
  // Flag that flips to `true` after the first successful fetch so we
  // don't blast the user with toasts for rows that were already
  // complete / failed when the app loaded.
  const seededRef = useRef(false);
  // Live list of in-progress rows — used to decide whether polling
  // should keep running.
  const hasInProgressRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    try {
      const rows = await apiGet("/reports/in-progress");
      if (!Array.isArray(rows)) return;

      // Merge remote status into the local map. For rows we already
      // knew about, detect transitions.
      const prev = lastStatusRef.current;
      const seen = new Set();

      rows.forEach((r) => {
        const id = r.report_id;
        if (!id) return;
        seen.add(id);
        const curr = (r.analysis_status || "").toLowerCase();
        const before = prev.get(id);
        if (!seededRef.current) {
          // First boot — just record the current status, do not toast.
          prev.set(id, curr);
          return;
        }
        if (before && before !== curr && TERMINAL_STATES.has(curr)) {
          emitTransitionToast(r, curr);
        }
        prev.set(id, curr);
      });

      // Anything that *was* analysing but is no longer returned by
      // /reports/in-progress has transitioned to complete (or failed).
      // Since the new endpoint only returns non-terminal rows, we use
      // its absence as the signal and re-fetch the individual row to
      // classify the final state.
      if (seededRef.current) {
        const stillAnalysing = Array.from(prev.keys()).filter(
          (id) => !seen.has(id),
        );
        for (const id of stillAnalysing) {
          const wasStatus = prev.get(id);
          prev.delete(id);
          if (!wasStatus || TERMINAL_STATES.has(wasStatus)) continue;
          // Fetch the now-terminal row so we can show the correct
          // toast. Fire-and-forget; any failure is silent.
          try {
            // The backend doesn't have a single-row /reports/:id fetch,
            // but /reports?limit=… gives us every row. Cheap enough
            // given it runs at most a couple of times per day.
            const all = await apiGet("/reports");
            const row = Array.isArray(all) ? all.find((r) => r.report_id === id) : null;
            if (row) {
              const finalStatus = (row.analysis_status || "").toLowerCase();
              emitTransitionToast(row, finalStatus);
            }
          } catch { /* ignore */ }
        }
      }

      hasInProgressRef.current = rows.length > 0;
      seededRef.current = true;
    } catch {
      // Swallow — polling will back off automatically via useSmartPoll.
      throw new Error("in-progress fetch failed");
    }
  }, []);

  // Reset the seen map when the user changes (login/logout).
  useEffect(() => {
    lastStatusRef.current = new Map();
    seededRef.current = false;
    hasInProgressRef.current = false;
  }, [user?.user_id]);

  // Always poll while authenticated — even if there's nothing in
  // progress right now the admin might trigger an upload at any
  // moment. `useSmartPoll` will only actually fire when the tab is
  // visible and there's no in-flight request, so the load stays tiny.
  useSmartPoll({
    fetch: fetchOnce,
    shouldPoll: () => true,
    intervalMs: 15_000,
    errorBackoffMs: 45_000,
    enabled: isAuthenticated,
  });

  return null;
};

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------
const _formatLabel = (row) => {
  const parts = [];
  if (row.company_code) parts.push(row.company_code);
  const typeCap = (row.report_type || "").replace(/^./, (c) => c.toUpperCase());
  if (typeCap) parts.push(`${typeCap} Report`);
  if (row.report_period) parts.push(row.report_period);
  return parts.join(" · ") || (row.file_name || "Report");
};

const emitTransitionToast = (row, status) => {
  const label = _formatLabel(row);
  if (status === "complete" || status === "analysed") {
    toast.success("Analysis complete!", {
      description: `${label} is ready to read.`,
      duration: 5000,
    });
  } else if (status === "failed" || status === "error") {
    toast.error("Analysis failed", {
      description: `${label}. Click Retry on the report row to try again.`,
      duration: 5000,
    });
  }
};

export default ReportAnalysisWatcher;
